import { Component, Input, OnInit, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import DataLabelsPlugin from 'chartjs-plugin-datalabels';
import { Chart, registerables } from 'chart.js';
import { ChartService } from 'src/app/services/charts/charts.service';
import { ChartReadyService } from 'src/app/services/charts/chart-ready.service';
import { MetadataService } from 'src/app/services/metadata/metadata.services';

Chart.register(...registerables, DataLabelsPlugin);

type IAxis = 'x' | 'y';
type ISort = 'asc' | 'desc' | 'default';
@Component({
  selector: 'app-bar-chart',
  templateUrl: './bar-chart.component.html',
  styleUrls: ['./bar-chart.component.scss'],
})
export class BarChartComponent implements OnInit, AfterViewInit {
  @Input() title = '';
  @Input() chartId: any;
  @Input() chartCategory: any;
  @Input() wideMode = false;
  @Input() subscribed = false;
  @Input() description = '';
  @Input() axis: IAxis = 'y';
  @Input() sort: ISort = 'default';
  @Input() isCustomChart = false;
  @Input() pdfMode = false;

  public dataSets: any;
  public chart: any;
  public labels: any;
  public colors: any;

  isEmpty = false;

  @ViewChild('chartCanvas') canvasEl!: ElementRef<HTMLCanvasElement>;

  public default = true;

  constructor(
    private chartService: ChartService,
    private meta: MetadataService,
    private chartReadyService: ChartReadyService
  ) { }

  ngOnInit(): void {
    if (this.chartId) {
      this.chartReadyService.registerChart(this.chartId);
    }
  }

  ngAfterViewInit() {
    this.getChartData();
    // initChart() se llama dentro de getChartData() después de un delay
  }

  getChartData() {
    this.dataSets = this.chartService.getChartDataByCategory(
      this.chartCategory
    );
    this.isEmpty = this.dataSets.every((value: number) => value === 0);
    this.labels = this.meta.getChartLabels(this.chartCategory);
    this.colors = this.meta.getChartColors(this.chartCategory);

    // Si está vacío, marcar como listo inmediatamente (no hay nada que renderizar)
    if (this.isEmpty && this.chartId) {
      console.log(`[BarChart ${this.chartId}] Empty data, marking ready immediately`);
      this.chartReadyService.markChartReady(this.chartId);
      return;
    }

    setTimeout(() => {
      this.initChart();
    }, 300);
  }

  initChart() {
    console.log(`[BarChart ${this.chartId}] initChart called. canvasEl=${!!this.canvasEl}, isCustomChart=${this.isCustomChart}, pdfMode=${this.pdfMode}`);

    if (!this.canvasEl) {
      console.warn(`[BarChart ${this.chartId}] Canvas not ready yet`);
      // No marcar como listo - esperar al próximo intento desde getChartData()
      return;
    }

    // Marcar como no listo mientras se inicializa
    if (this.chartId) {
      this.chartReadyService.markChartNotReady(this.chartId);
    }

    const ctx = this.canvasEl.nativeElement.getContext('2d');
    if (!ctx) {
      console.error(`[BarChart ${this.chartId}] No se pudo obtener el contexto del canvas`);
      // Marcar como listo para evitar timeout
      if (this.chartId) {
        this.chartReadyService.markChartReady(this.chartId);
      }
      return;
    }

    // Si no es customChart, marcar como listo y salir
    if (!this.isCustomChart) {
      console.log(`[BarChart ${this.chartId}] Not custom chart, marking ready`);
      if (this.chartId) {
        this.chartReadyService.markChartReady(this.chartId);
      }
      return;
    }

    // Solo para isCustomChart = true
    if (this.isCustomChart) {
      let emptySet = 0;
      this.dataSets.forEach((data: any) => {
        emptySet = data === 0 ? emptySet + 1 : emptySet;
      });

      this.default = emptySet === this.dataSets.length;

      if (this.default) {
        this.dataSets = [
          0.25, 0.25, 0.25, 0.25, 0.25
        ];
        this.colors = ['#FFFFF'];
      }

      const ratio = window.devicePixelRatio || 1;
      this.canvasEl.nativeElement.width = this.canvasEl.nativeElement.offsetWidth * ratio;
      this.canvasEl.nativeElement.height = this.canvasEl.nativeElement.offsetHeight * ratio;
      ctx.scale(ratio, ratio);

      const labels = this.labels;
      const data = {
        labels: labels,
        datasets: [{
          axis: 'y',
          label: '',
          data: this.dataSets,
          fill: true,
          backgroundColor: this.colors,
          borderColor: this.colors,
          borderWidth: 0,
          borderRadius: 5
        }]
      };

      // Configuración de animación basada en pdfMode
      const animationConfig = this.pdfMode ? false : {
        duration: 1000,
        onComplete: () => {
          if (this.chartId) {
            this.chartReadyService.markChartReady(this.chartId);
          }
        }
      };

      this.chart = new Chart(ctx, {
        type: 'bar',
        data: data,
        options: {
          indexAxis: 'y',
          animation: animationConfig as any,
          scales: {
            x: {
              ticks: {
                callback: (value) => value + '%',
              }
            }
          },
          layout: {
            padding: {
              right: 50
            }
          },
          plugins: {
            legend: {
              display: false
            },
            datalabels: {
              color: '#222',
              font: {
                weight: 'lighter',
                size: 12,
              },
              anchor: 'end',
              align: 'end',
              formatter: (value) => value + '%',
            }
          }
        },
      });

      console.log(`[BarChart ${this.chartId}] Chart created successfully. pdfMode=${this.pdfMode}`);

      // Si modo PDF (sin animación), marcar como listo después del render
      if (this.pdfMode && this.chartId) {
        console.log(`[BarChart ${this.chartId}] Scheduling markChartReady via requestAnimationFrame`);
        requestAnimationFrame(() => {
          console.log(`[BarChart ${this.chartId}] requestAnimationFrame callback - marking ready`);
          this.chartReadyService.markChartReady(this.chartId);
        });
      }
    }
  }

  reInitChart() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
    this.getChartData();
    // Eliminada la llamada directa a initChart() - ya se llama dentro de getChartData()
  }
}
